/**
 * Admin-only: who is using Northstar, and how.
 *
 * Two ledgers behind it (V016): every MCP tool call with its arguments and
 * result (NORTHSTAR_MCP_CALLS) and every HTTP request under /api and /mcp
 * (NORTHSTAR_API_REQUESTS). Spend lives in the two panels above this one; this
 * is the request side: calls, people, failures, latency, and the exact call
 * behind a failure.
 */
import { useMemo, useState } from "react";
import { Activity, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRecentToolCalls, useUsage } from "@/lib/useAdmin";
import type { ApiRequestRow, ToolCall, ToolCallRow } from "@/lib/types";
import { cn } from "@/lib/utils";

interface Bucket {
  label: string;
  calls: number;
  errors: number;
  latencyWeighted: number;
  maxLatency: number;
}

function rollup(rows: ToolCallRow[], label: (row: ToolCallRow) => string): Bucket[] {
  const buckets = new Map<string, Bucket>();
  for (const row of rows) {
    const key = label(row);
    const b = buckets.get(key) ?? { label: key, calls: 0, errors: 0, latencyWeighted: 0, maxLatency: 0 };
    b.calls += row.calls;
    b.errors += row.errors;
    b.latencyWeighted += row.avg_latency_ms * row.calls;
    b.maxLatency = Math.max(b.maxLatency, row.max_latency_ms);
    buckets.set(key, b);
  }
  return [...buckets.values()].sort((a, b) => b.calls - a.calls);
}

function rollupRequests(rows: ApiRequestRow[]): Bucket[] {
  const buckets = new Map<string, Bucket>();
  for (const row of rows) {
    const key = `${row.method} ${row.route}`;
    const b = buckets.get(key) ?? { label: key, calls: 0, errors: 0, latencyWeighted: 0, maxLatency: 0 };
    b.calls += row.requests;
    if (row.status_class >= 500) b.errors += row.requests;
    b.latencyWeighted += row.avg_latency_ms * row.requests;
    b.maxLatency = Math.max(b.maxLatency, row.max_latency_ms);
    buckets.set(key, b);
  }
  return [...buckets.values()].sort((a, b) => b.calls - a.calls);
}

function seconds(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`;
}

function BucketTable({ title, rows, unit, limit }: { title: string; rows: Bucket[]; unit: string; limit?: number }) {
  if (rows.length === 0) return null;
  const shown = limit ? rows.slice(0, limit) : rows;
  return (
    <div className="flex flex-col gap-1">
      <span className="font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted">{title}</span>
      {shown.map((row) => (
        <div key={row.label} className="flex items-baseline justify-between gap-3 font-sans text-xs">
          <span className="truncate text-bw-ink">{row.label}</span>
          <span className="shrink-0 text-bw-ink-muted">
            <span className="font-semibold text-bw-navy">{row.calls}</span> {unit}
            {row.errors > 0 && <span className="text-bw-danger"> · {row.errors} failed</span>}
            {row.calls > 0 && <> · avg {seconds(row.latencyWeighted / row.calls)}</>}
            {row.maxLatency > 0 && <> · max {seconds(row.maxLatency)}</>}
          </span>
        </div>
      ))}
      {limit && rows.length > limit && (
        <span className="font-sans text-[11px] text-bw-ink-muted">and {rows.length - limit} more</span>
      )}
    </div>
  );
}

function pretty(json: string | null): string {
  if (!json) return "";
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}

function formatWhen(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

/** One call: who, tool, when, and the arguments and result on demand. */
function CallRow({ call }: { call: ToolCall }) {
  const [open, setOpen] = useState(false);
  const failed = call.status === "error";
  return (
    <div className="flex flex-col gap-1 border-b border-bw-blue-muted/30 py-2 last:border-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2 font-sans text-xs">
          <span className={cn("font-semibold", failed ? "text-bw-danger" : "text-bw-navy")}>{call.tool}</span>
          <span className="truncate text-bw-ink-muted">
            {call.user_upn ?? "(unknown)"}
            {call.project && <> · {call.project}</>}
            {call.latency_ms != null && <> · {seconds(call.latency_ms)}</>}
            {call.created_at && <> · {formatWhen(call.created_at)}</>}
          </span>
        </span>
        <Button variant="ghost" size="sm" onClick={() => setOpen(!open)}>
          {open ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
          {open ? "Hide" : "Details"}
        </Button>
      </div>
      {failed && call.error && <p className="font-sans text-xs text-bw-danger">{call.error}</p>}
      {open && (
        <div className="flex flex-col gap-2 rounded-card border border-bw-blue-muted/40 p-3">
          {call.client && <span className="font-sans text-[11px] text-bw-ink-muted">client: {call.client}</span>}
          <span className="font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted">Arguments</span>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap font-mono text-[11px] text-bw-ink">
            {pretty(call.arguments) || "(none)"}
          </pre>
          {call.result && (
            <>
              <span className="font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted">Result</span>
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap font-mono text-[11px] text-bw-ink">
                {pretty(call.result)}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RecentCalls() {
  const [open, setOpen] = useState(false);
  const calls = useRecentToolCalls(open);
  return (
    <div className="flex flex-col gap-1">
      <Button variant="outline" size="sm" className="w-fit" onClick={() => setOpen(!open)}>
        {open ? "Hide recent calls" : "Show the last 100 calls"}
      </Button>
      {open && calls.isLoading && <span className="font-sans text-xs text-bw-ink-muted">Loading…</span>}
      {open && calls.isError && <span className="font-sans text-xs text-bw-ink-muted">Couldn't load calls.</span>}
      {open && calls.data && calls.data.length === 0 && (
        <span className="font-sans text-xs text-bw-ink-muted">No tool calls recorded yet.</span>
      )}
      {open && calls.data?.map((call) => <CallRow key={call.call_id} call={call} />)}
    </div>
  );
}

export function AdminUsage() {
  const { data, isLoading, isError } = useUsage(30);
  const rows = useMemo(() => data?.tool_calls ?? [], [data]);
  const byTool = useMemo(() => rollup(rows, (r) => r.tool), [rows]);
  const byPerson = useMemo(() => rollup(rows, (r) => r.user_upn ?? "(unknown)"), [rows]);
  const byDay = useMemo(
    () => rollup(rows, (r) => r.day ?? "").sort((a, b) => b.label.localeCompare(a.label)).slice(0, 7),
    [rows],
  );
  const byRoute = useMemo(() => rollupRequests(data?.api_requests ?? []), [data]);
  const totals = useMemo(
    () => ({
      calls: rows.reduce((n, r) => n + r.calls, 0),
      errors: rows.reduce((n, r) => n + r.errors, 0),
      people: new Set(rows.map((r) => r.user_upn ?? "")).size,
      requests: (data?.api_requests ?? []).reduce((n, r) => n + r.requests, 0),
    }),
    [rows, data],
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Activity className="h-4 w-4 text-bw-blue" aria-hidden />
          Usage
        </CardTitle>
        <p className="font-sans text-xs text-bw-ink-muted">
          Every MCP tool call (with what was asked and what came back) and every API request over the last 30
          days. Spend is in the two panels above; this is who did what, what failed, and how long it took.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pt-0">
        {isLoading && <p className="font-sans text-sm text-bw-ink-muted">Loading usage…</p>}
        {isError && <p className="font-sans text-sm text-bw-ink-muted">Couldn't load usage.</p>}
        {data && rows.length === 0 && totals.requests === 0 && (
          <p className="font-sans text-sm text-bw-ink-muted">Nothing recorded in the last 30 days.</p>
        )}
        {data && (rows.length > 0 || totals.requests > 0) && (
          <>
            <div className="flex items-baseline gap-2">
              <span className="font-sans text-2xl font-bold text-bw-navy">{totals.calls}</span>
              <span className="font-sans text-xs text-bw-ink-muted">
                tool calls by {totals.people} {totals.people === 1 ? "person" : "people"}
                {totals.errors > 0 && (
                  <>
                    {" "}
                    · <span className="text-bw-danger">{totals.errors} failed</span>
                  </>
                )}
                {" "}· {totals.requests} API requests
              </span>
            </div>
            <BucketTable title="By tool" rows={byTool} unit="calls" />
            <BucketTable title="By person" rows={byPerson} unit="calls" />
            <BucketTable title="Last 7 active days" rows={byDay} unit="calls" />
            <BucketTable title="API requests by route" rows={byRoute} unit="requests" limit={12} />
            {data.recent_failures.length > 0 && (
              <div className="flex flex-col gap-1">
                <span className="font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted">
                  Recent failures
                </span>
                {data.recent_failures.map((call) => (
                  <CallRow key={call.call_id} call={call} />
                ))}
              </div>
            )}
            <RecentCalls />
          </>
        )}
      </CardContent>
    </Card>
  );
}
